<template>
  <Transition name="backdrop-fade">
    <div
      v-if="open"
      class="preorder-modal-backdrop"
      aria-hidden="true"
      @click="close"
    />
  </Transition>
  <Transition name="panel-slide">
    <div v-if="open" class="preorder-modal-container" @click.self="close">
      <div
        class="preorder-modal-panel"
        :class="{ 'preorder-modal-panel--final-sale': isFinalSale }"
        role="dialog"
        aria-labelledby="preorder-modal-title"
        aria-modal="true"
        @click.stop
      >
        <IconButton
          theme="secondary"
          icon="cross"
          class="preorder-modal-panel__close"
          aria-label="Close"
          @click="close"
        />
        <div
          class="text-box"
          :class="{ 'text-box--uk-warehouse': isUkWarehouse }"
        >
          <h2
            id="preorder-modal-title"
            class="preorder-modal-panel__title"
            :class="{
              'preorder-modal-panel__title--uk-warehouse': isUkWarehouse,
            }"
          >
            {{
              isUkWarehouse
                ? "Shipped from the UK"
                : isFinalSale
                ? "Final Sale"
                : "Our Pre-Order System"
            }}
          </h2>
          <p
            v-if="!isFinalSale && !isUkWarehouse"
            class="preorder-modal-panel__subtitle"
          >
            Why Choose Pre-Order?
          </p>
          <p class="preorder-modal-panel__body">
            {{
              isUkWarehouse
                ? ukWarehouseDeliveryMessage
                : isFinalSale
                ? "Final Sale Items: These are not eligible for return, except where required by law. For more details, please refer to our FAQs."
                : "We create intentionally, not excessively. By releasing our collections in limited quantities we're able to reduce waste and make more considered choices. Pre-order this piece today and we'll ship it to you as soon as it arrives."
            }}
          </p>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import IconButton from "@/components/IconButton.vue";
import { mapState, mapActions } from "vuex";
import { UK_WAREHOUSE_DELIVERY_MESSAGE } from "@/helpers/ukWarehouse.js";

export default {
  name: "PreorderVariantModal",
  components: {
    IconButton,
  },
  computed: {
    ...mapState("product", [
      "openPreorderModal",
      "openFinalSaleVariantModal",
      "openUkWarehouseVariantModal",
    ]),
    ukWarehouseDeliveryMessage() {
      return UK_WAREHOUSE_DELIVERY_MESSAGE;
    },
    open() {
      return (
        this.openPreorderModal ||
        this.openFinalSaleVariantModal ||
        this.openUkWarehouseVariantModal
      );
    },
    isFinalSale() {
      return (
        this.openFinalSaleVariantModal &&
        !this.openPreorderModal &&
        !this.openUkWarehouseVariantModal
      );
    },
    isUkWarehouse() {
      return (
        this.openUkWarehouseVariantModal &&
        !this.openPreorderModal &&
        !this.openFinalSaleVariantModal
      );
    },
  },
  methods: {
    ...mapActions("product", [
      "setOpenPreorderModal",
      "setOpenFinalSaleVariantModal",
      "setOpenUkWarehouseVariantModal",
    ]),
    close() {
      this.setOpenPreorderModal(false);
      this.setOpenFinalSaleVariantModal(false);
      this.setOpenUkWarehouseVariantModal(false);
    },
  },
};
</script>

<style lang="scss" scoped>
.preorder-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.2);
  will-change: opacity;
}

.preorder-modal-container {
  position: fixed;
  inset: 0;
  z-index: 1001;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
  pointer-events: none;

  @include respond-above(md) {
    align-items: center;
    padding: 20px;
  }
}

.preorder-modal-panel {
  position: relative;
  width: 100%;
  max-width: none;
  background: $color-white;
  padding: 50px 20px 60px;
  text-align: center;
  box-sizing: border-box;
  pointer-events: auto;
  will-change: transform, opacity;

  @include respond-above(md) {
    max-width: 384px;
    padding: 68px 0 62px;
  }

  .text-box {
    color: #5f8e5c;
    margin: 0 auto;

    @include respond-above(md) {
      max-width: 308px;
    }
  }

  :deep(.button.preorder-modal-panel__close.button--secondary) {
    cursor: pointer;
  }

  &__close {
    position: absolute;
    top: 12px;
    right: 12px;
    padding: 10px;
    border-radius: 50%;

    @include respond-above(md) {
      top: 16px;
      right: 16px;
    }
  }

  &__title {
    @include editorial-headline(20px);
    text-transform: uppercase;
    margin: 0 0 12px;
    color: #5f8e5c;

    @include respond-above(md) {
      @include editorial-headline(24px);
      margin-bottom: 16px;
    }

    &--uk-warehouse {
      text-transform: none;
    }
  }

  &__subtitle {
    @include editorial-body(14px);
    margin: 0 0 28px;
    color: #5f8e5c;
  }

  &__body {
    @include utility-body(13px);
    text-wrap: pretty;
    color: #5f8e5c;
  }

  &--final-sale {
    .text-box,
    .preorder-modal-panel__title,
    .preorder-modal-panel__body {
      color: $color-red;
    }
  }
}

.text-box--uk-warehouse,
.preorder-modal-panel .text-box--uk-warehouse .preorder-modal-panel__title,
.preorder-modal-panel .text-box--uk-warehouse .preorder-modal-panel__body {
  color: $color-black;
}

.backdrop-fade-enter-active,
.backdrop-fade-leave-active {
  transition: opacity 0.4s ease;
}

.backdrop-fade-enter-from,
.backdrop-fade-leave-to {
  opacity: 0;
}

.panel-slide-enter-active,
.panel-slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.4s ease;
}

.panel-slide-enter-from,
.panel-slide-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

@include respond-above(md) {
  .panel-slide-enter-from,
  .panel-slide-leave-to {
    transform: translateY(20px);
  }
}
</style>
